import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { 
  Car, CheckCircle, ArrowRight, Camera, AlertTriangle, 
  Package, PackageCheck, Calendar, ChevronLeft, ChevronRight, Gauge
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format, startOfWeek, addDays, isSameDay, addWeeks, subWeeks, getWeek } from "date-fns";
import { fr } from "date-fns/locale";

export default function CollaborateurDashboard() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [overdueVehicles, setOverdueVehicles] = useState([]);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [notes, setNotes] = useState("");
  const [kilometrage, setKilometrage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Pour signaler un problème
  const [problemDialog, setProblemDialog] = useState(false);
  const [problemVehicle, setProblemVehicle] = useState(null);
  const [problemMessage, setProblemMessage] = useState("");
  const [isBlocking, setIsBlocking] = useState(false);
  const [problemPhoto, setProblemPhoto] = useState(null);
  
  // Références pour les inputs caméra
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  
  const navigate = useNavigate();
  
  // Mécanicien ne voit pas les infos client
  const isMecanicien = user?.specialite === "mecanicien";

  useEffect(() => {
    fetchData();
  }, [currentWeek]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDate = format(currentWeek, "yyyy-MM-dd");
      const endDate = format(addDays(currentWeek, 6), "yyyy-MM-dd");
      const today = format(new Date(), "yyyy-MM-dd");
      
      const [vehiclesRes, stepsRes] = await Promise.all([
        api.get(`/vehicles?start_date=${startDate}&end_date=${endDate}&include_ongoing=true`),
        api.get("/workflow-steps")
      ]);
      
      setWorkflowSteps(stepsRes.data);
      
      // Filtrer les véhicules pour la spécialité de l'utilisateur
      const userSteps = (stepsRes.data || [])
        .filter(s => s.specialite === user?.specialite)
        .map(s => s.name);
      
      const allVehicles = vehiclesRes.data || [];
      
      const filteredForUser = allVehicles.filter(v => {
        // Exclure les terminés
        if (v.workflow_step === "termine" || v.workflow_step === "livraison_facturation") {
          return false;
        }
        // Pour les mécaniciens
        if (isMecanicien) {
          return v.type_intervention === "mecanique" || v.workflow_step === "entretien_mecanique";
        }
        // Pour les autres, filtrer par étape workflow
        return userSteps.includes(v.workflow_step);
      });
      
      // Séparer les véhicules en retard (pour les stats uniquement)
      const overdue = filteredForUser.filter(v => {
        if (!v.date_rdv) return false;
        return v.date_rdv < today;
      });
      
      setVehicles(filteredForUser); // Tous les véhicules (y compris en retard)
      setOverdueVehicles(overdue); // Pour les stats seulement
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const getCurrentStepInfo = (vehicle = null) => {
    if (vehicle && vehicle.workflow_step && vehicle.workflow_step !== "none") {
      // Dynamic step info from actual workflow
      const currentStep = workflowSteps.find(s => s.name === vehicle.workflow_step);
      const stepNames = workflowSteps.map(s => s.name);
      const currentIdx = stepNames.indexOf(vehicle.workflow_step);
      let nextLabel = "Terminé";
      if (currentIdx >= 0 && currentIdx < stepNames.length - 1) {
        nextLabel = workflowSteps[currentIdx + 1]?.label || "Suivant";
      }
      return { label: currentStep?.label || vehicle.workflow_step, next: nextLabel };
    }
    if (user?.specialite === "mecanicien") {
      return { label: "Mécanique", next: "Terminé" };
    }
    if (user?.specialite === "carrossier") {
      return { label: "Carrosserie", next: "Suivant" };
    }
    if (user?.specialite === "peintre") {
      return { label: "Peinture", next: "Suivant" };
    }
    if (user?.specialite === "preparateur_peintre") {
      return { label: "Préparation Peinture", next: "Suivant" };
    }
    const specialiteLabels = {
      "carrossier": "Carrosserie",
      "peintre": "Peinture",
      "mecanicien": "Mécanique",
      "preparateur_peintre": "Préparation Peinture"
    };
    return { label: specialiteLabels[user?.specialite] || "Mes tâches", next: "Suivant" };
  };

  const stepInfo = getCurrentStepInfo();

  // Générer les jours de la semaine (Lun-Ven)
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      days.push(addDays(currentWeek, i));
    }
    return days;
  };

  // Grouper les véhicules par jour
  const getVehiclesForDay = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isToday = isSameDay(day, today);
    const isCurrentWeek = isSameDay(startOfWeek(currentWeek, { weekStartsOn: 1 }), startOfWeek(new Date(), { weekStartsOn: 1 }));
    
    return vehicles.filter(v => {
      if (v.statut === "termine") return false;
      if (!v.date_rdv) return false;
      
      const rdvDate = new Date(v.date_rdv);
      rdvDate.setHours(0, 0, 0, 0);
      
      // Véhicule en retard (date passée)
      const vehicleIsOverdue = rdvDate < today;
      
      // Les véhicules en retard apparaissent sur AUJOURD'HUI
      if (isCurrentWeek && vehicleIsOverdue) {
        return isToday;
      }
      
      // Sinon, afficher sur la date du RDV
      return isSameDay(rdvDate, day);
    });
  };
  
  // Vérifier si un véhicule est en retard
  const isOverdue = (vehicle) => {
    if (!vehicle.date_rdv) return false;
    const today = format(new Date(), "yyyy-MM-dd");
    return vehicle.date_rdv < today;
  };

  const handleAdvance = async () => {
    if (!selectedVehicle) return;
    
    // Pour les mécaniciens, le kilométrage est obligatoire
    if (isMecanicien && !kilometrage) {
      toast.error("Veuillez saisir le kilométrage du véhicule");
      return;
    }
    
    setProcessing(true);
    
    try {
      // Si mécanicien et kilométrage fourni, mettre à jour le véhicule d'abord
      if (isMecanicien && kilometrage) {
        await api.put(`/vehicles/${selectedVehicle.id}/kilometrage`, { 
          kilometrage: parseInt(kilometrage) 
        });
      }
      
      if (selectedVehicle.workflow_step === "none") {
        await api.post(`/workflow/complete-mecanique/${selectedVehicle.id}`);
      } else {
        await api.post("/workflow/advance", {
          vehicle_id: selectedVehicle.id,
          notes: notes
        });
      }
      toast.success("Étape validée !");
      setSelectedVehicle(null);
      setNotes("");
      setKilometrage("");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message).join(", ") : (typeof detail === 'object' ? JSON.stringify(detail) : detail);
      toast.error(msg || "Erreur");
    }
    setProcessing(false);
  };

  const handleReportProblem = async () => {
    if (!problemVehicle || !problemMessage) {
      toast.error("Entrez un message");
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/vehicles/${problemVehicle.id}/comments`, {
        vehicle_id: problemVehicle.id,
        message: problemMessage,
        is_blocking: isBlocking,
        photo_base64: problemPhoto
      });
      toast.success(isBlocking ? "Dossier marqué comme bloqué" : "Commentaire ajouté");
      setProblemDialog(false);
      setProblemVehicle(null);
      setProblemMessage("");
      setIsBlocking(false);
      setProblemPhoto(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
      console.error(error);
    }
    setProcessing(false);
  };

  // Gestion des photos
  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 800;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
        setProblemPhoto(compressedBase64);
        toast.success("Photo ajoutée");
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };

  const getStepColor = (step) => {
    const stepData = workflowSteps.find(s => s.name === step);
    if (stepData) return `step-${stepData.color}`;
    return "bg-slate-100 text-slate-600";
  };

  const pendingVehicles = vehicles.filter(v => v.statut !== "termine");
  const completedVehicles = vehicles.filter(v => v.statut === "termine");
  const weekDays = getWeekDays();

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 animate-fade-in" data-testid="collaborateur-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-3xl font-black text-slate-900 tracking-tight">
            {stepInfo.label}
          </h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">
            {pendingVehicles.length} véhicule(s) à traiter
          </p>
        </div>
        
      {/* Navigation semaine pour tous les collaborateurs */}
      <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2 w-fit">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-bold text-slate-700 min-w-[60px] text-center">
          S{getWeek(currentWeek, { weekStartsOn: 1 })}
        </span>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>

      {/* Stats - responsive grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-2 md:p-4 flex items-center gap-2 md:gap-4">
            <div className="p-2 md:p-3 bg-blue-100 rounded-lg">
              <Car className="h-4 w-4 md:h-6 md:w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-lg md:text-2xl font-bold text-slate-900">{vehicles.length + overdueVehicles.length}</p>
              <p className="text-xs md:text-sm text-slate-600">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-slate-200 ${overdueVehicles.length > 0 ? 'border-red-300 bg-red-50' : ''}`}>
          <CardContent className="p-2 md:p-4 flex items-center gap-2 md:gap-4">
            <div className={`p-2 md:p-3 rounded-lg ${overdueVehicles.length > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
              <AlertTriangle className={`h-4 w-4 md:h-6 md:w-6 ${overdueVehicles.length > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <p className={`text-lg md:text-2xl font-bold ${overdueVehicles.length > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                {overdueVehicles.length}
              </p>
              <p className="text-xs md:text-sm text-slate-600">En retard</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-2 md:p-4 flex items-center gap-2 md:gap-4">
            <div className="p-2 md:p-3 bg-amber-100 rounded-lg">
              <Car className="h-4 w-4 md:h-6 md:w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-lg md:text-2xl font-bold text-slate-900">{pendingVehicles.length}</p>
              <p className="text-xs md:text-sm text-slate-600">Cette semaine</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-2 md:p-4 flex items-center gap-2 md:gap-4">
            <div className="p-2 md:p-3 bg-emerald-100 rounded-lg">
              <CheckCircle className="h-4 w-4 md:h-6 md:w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-lg md:text-2xl font-bold text-slate-900">{completedVehicles.length}</p>
              <p className="text-xs md:text-sm text-slate-600">Terminés</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vue par jour pour tous les collaborateurs */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Planning de la semaine
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="spinner" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {weekDays.map((day) => {
              const dayVehicles = getVehiclesForDay(day);
              const isToday = isSameDay(day, new Date());
              
              return (
                <Card 
                  key={day.toISOString()} 
                  className={`border-slate-200 ${isToday ? 'ring-2 ring-red-400 border-red-400' : ''}`}
                >
                  <CardHeader className={`py-2 px-3 ${isToday ? 'bg-red-50' : 'bg-slate-50'}`}>
                    <CardTitle className="text-sm font-bold flex items-center justify-between">
                      <span className={isToday ? 'text-red-600' : 'text-slate-700'}>
                        {format(day, "EEEE", { locale: fr })}
                      </span>
                      <span className={`text-lg ${isToday ? 'text-red-600' : 'text-slate-900'}`}>
                        {format(day, "d", { locale: fr })}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 space-y-2 min-h-[150px]">
                    {dayVehicles.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-4">Aucun RDV</p>
                    ) : (
                      dayVehicles.map((vehicle) => {
                        const vehicleIsOverdue = isOverdue(vehicle);
                        return (
                          <div
                            key={vehicle.id}
                            className={`p-2 rounded-lg border text-xs cursor-pointer transition-all hover:border-red-400 ${
                              vehicle.is_blocked 
                                ? "border-red-300 bg-red-50" 
                                : vehicleIsOverdue
                                ? "border-red-300 bg-red-50/50"
                                : "border-slate-200 bg-white"
                            }`}
                            onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <span className="font-mono font-bold text-slate-900">
                                  {vehicle.immatriculation}
                                </span>
                                {vehicleIsOverdue && (
                                  <Badge className="bg-red-500 text-white text-[9px] px-1 py-0 h-4 animate-pulse">
                                    En retard
                                  </Badge>
                                )}
                              </div>
                              <span className={`font-bold ${vehicleIsOverdue ? 'text-red-600' : 'text-blue-600'}`}>
                                {vehicleIsOverdue ? format(new Date(vehicle.date_rdv), "dd/MM") : vehicle.heure_rdv}
                              </span>
                            </div>
                            <p className="text-slate-600 truncate">
                              {vehicle.marque} {vehicle.modele}
                            </p>
                            {vehicle.description && (
                              <p className="text-slate-500 truncate italic text-[10px]">
                                {vehicle.description}
                              </p>
                            )}
                            {/* Afficher client seulement si pas mécanicien */}
                            {!isMecanicien && vehicle.client?.nom && (
                              <p className="text-slate-500 truncate text-[10px] font-medium">
                                {vehicle.client.nom} {vehicle.client.prenom}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-1">
                              <div className="flex items-center gap-1">
                                {vehicle.pieces_commandees ? (
                                  <PackageCheck className="h-3 w-3 text-emerald-500" />
                                ) : (
                                  <Package className="h-3 w-3 text-red-500" />
                                )}
                                {vehicle.is_blocked && (
                                  <AlertTriangle className="h-3 w-3 text-red-500" />
                                )}
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-1 text-amber-600 hover:bg-amber-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProblemVehicle(vehicle);
                                    setProblemDialog(true);
                                  }}
                                  title="Signaler un problème"
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-6 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedVehicle(vehicle);
                                  }}
                                  disabled={vehicle.is_blocked}
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  OK
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Advance Dialog */}
      <AlertDialog open={!!selectedVehicle} onOpenChange={() => setSelectedVehicle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Valider l'étape</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedVehicle && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p className="font-mono font-bold text-slate-900">
                    {selectedVehicle.immatriculation}
                  </p>
                  <p className="text-sm">
                    {selectedVehicle.marque} {selectedVehicle.modele}
                  </p>
                </div>
              )}
              {selectedVehicle && (() => {
                const info = getCurrentStepInfo(selectedVehicle);
                return (
                  <div className="mt-4 flex items-center gap-2 text-sm">
                    <Badge className={getStepColor(selectedVehicle?.workflow_step)}>
                      {info.label}
                    </Badge>
                    <ArrowRight className="h-4 w-4" />
                    <Badge variant="outline">{info.next}</Badge>
                  </div>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-2 space-y-4">
            {/* Champ kilométrage pour les mécaniciens */}
            {isMecanicien && (
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Gauge className="h-4 w-4" />
                  Kilométrage *
                </Label>
                <Input
                  type="number"
                  placeholder="Ex: 125000"
                  value={kilometrage}
                  onChange={(e) => setKilometrage(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Obligatoire pour valider l'intervention
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium">Notes (optionnel)</Label>
              <Textarea
                placeholder="Ajouter des notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAdvance}
              disabled={processing || (isMecanicien && !kilometrage)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {processing ? <div className="spinner" /> : "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Problem Report Dialog avec accès caméra */}
      <AlertDialog open={problemDialog} onOpenChange={(open) => {
        if (!open) {
          setProblemDialog(false);
          setProblemVehicle(null);
          setProblemMessage("");
          setIsBlocking(false);
          setProblemPhoto(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Signaler un problème
            </AlertDialogTitle>
            <AlertDialogDescription>
              {problemVehicle && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p className="font-mono font-bold text-slate-900">
                    {problemVehicle.immatriculation}
                  </p>
                  <p className="text-sm text-slate-600">
                    {problemVehicle.marque} {problemVehicle.modele}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-2">
            <div>
              <Label>Description du problème *</Label>
              <Textarea
                placeholder="Ex: Manque pièce référence XYZ à commander..."
                value={problemMessage}
                onChange={(e) => setProblemMessage(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div>
                <Label className="font-medium text-red-900">Dossier bloqué ?</Label>
                <p className="text-xs text-red-700">Alerte envoyée à la gestion</p>
              </div>
              <Switch
                checked={isBlocking}
                onCheckedChange={setIsBlocking}
              />
            </div>
            
            {/* Section photo avec bouton caméra */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Ajouter une photo
              </Label>
              
              {/* Inputs cachés pour caméra et galerie */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                className="hidden"
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={handleCameraCapture}
                className="hidden"
              />
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Prendre photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => galleryInputRef.current?.click()}
                >
                  Galerie
                </Button>
              </div>
              
              {problemPhoto && (
                <div className="relative">
                  <img src={problemPhoto} alt="Preview" className="mt-2 max-h-40 rounded-lg w-full object-cover" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-4 right-2"
                    onClick={() => setProblemPhoto(null)}
                  >
                    Supprimer
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReportProblem}
              disabled={processing || !problemMessage}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {processing ? <div className="spinner" /> : "Envoyer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
